// Basic
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import PropTypes from 'prop-types';
// Libs
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
// Components
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/common-utils/components/ui/form';
import Entity from '@/common-utils/components/renders/entity/Entity';
import { Input } from '@/common-utils/components/ui/input';
import { useTranslation } from 'react-i18next';
// Lang
import { nsMerchantUser } from '@/app/i18n/namespaces';

/**
 * @param {function} setValidForm
 * @param {function} setFormData
 * @returns {JSX} AddApiKeyForm
 */
function AddAlmaApiKeyForm({ setValidForm, setFormData }) {
    // Translation
    const { t: translate } = useTranslation();

    // Constants
    const labelTicketTpeSchema = z
        .string()
        .max(12)
        .refine((value) => !value || value && /^.{1,12}$/.test(value))
        .refine((value) => !value || value && /^\w+$/.test(value.split(' ').join('')), { message: translate('form.alma.label') });
    const apiKeySchema = z.string().min(1, { message: translate('form.alma.api_key') });

    const formSchema = z.object({
        apiKey: apiKeySchema,
        labelTicketTpe: labelTicketTpeSchema
    });

    // Form
    const form = useForm({
        resolver: zodResolver(formSchema),
        mode: 'onChange',
        defaultValues: {
            apiKey: '',
            labelTicketTpe: ''
        }
    });

    // Hooks
    useEffect(() => {
        if (form.formState.isValid) {
            setValidForm(true);
            setFormData(form.getValues());
        } else {
            setValidForm(false);
        }
    }, [form.formState.isValid, form.getValues()]);

    return (
        <Form {...form}>
            <form className='login-form space-y-4'>
                <FormField
                    control={form.control}
                    name='apiKey'
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel required>
                                <Entity
                                    namespace={nsMerchantUser}
                                    entity='modal.add_external_contract.alma.api_key'
                                />
                            </FormLabel>
                            <FormControl>
                                <Input namespace={nsMerchantUser} entity='' {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <FormField
                    control={form.control}
                    name='labelTicketTpe'
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>
                                <Entity
                                    namespace={nsMerchantUser}
                                    entity='modal.add_external_contract.alma.pos_ticket_label'
                                />
                                <Entity
                                    namespace={nsMerchantUser}
                                    entity='modal.add_external_contract.alma.pos_ticket_label_indication'
                                    className='ml-2 font-normal text-xs italic'
                                />
                            </FormLabel>
                            <FormControl>
                                <Input
                                    namespace={nsMerchantUser}
                                    entity='modal.add_external_contract.alma.pos_ticket_label_placeholder'
                                    {...field}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
            </form>
        </Form>
    );
}

AddAlmaApiKeyForm.propTypes = {
    setValidForm: PropTypes.func,
    setFormData: PropTypes.func
};

export default AddAlmaApiKeyForm;